import React from 'react';
import { Mail } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { FenetreEmailsModule } from '../ui/LigneNotification';
import { Segmented } from '../ui/Field';
import { NotificationBlock, insertNotificationToken } from '../PrestationPlanning/PrestationNotificationsPage';

// Jetons RÉELLEMENT disponibles dans chaque email (cf. `vars` construits dans POST /subscriptions/:id/send
// et notifierEtablissementSubscription, functions/api/[[path]].js) — jamais la liste générique
// ALL_NOTIFICATION_TOKENS, sans rapport avec un contrat d'abonnement.
const SUBSCRIPTION_SEND_TOKENS = [
    { token: '{{nom_client}}', label: 'Nom du client' },
    { token: '{{formule}}', label: 'Formule souscrite' },
    { token: '{{nb_passages}}', label: "Nombre de passages par an" },
    { token: '{{entreprise}}', label: 'Nom de votre établissement' },
    { token: '{{bouton}}', label: 'Bouton "Consulter et signer" (cliquez où l\'insérer)' },
    { token: '{{logo}}', label: 'Votre logo' },
];
const SUBSCRIPTION_SIGNED_TOKENS = [
    { token: '{{nom_client}}', label: 'Nom du client' },
    { token: '{{formule}}', label: 'Formule souscrite' },
    { token: '{{entreprise}}', label: 'Nom de votre établissement' },
];
const SUBSCRIPTION_DECLINED_TOKENS = [
    ...SUBSCRIPTION_SIGNED_TOKENS,
    { token: '{{motif}}', label: 'Motif indiqué par le client' },
];

/**
 * Réglages des emails du module Abonnements, ouverts directement depuis le module (bouton « Paramètres »
 * de SubscriptionsModule.tsx) — même principe que DevisEmailSettingsModal.tsx côté Suivi de prestation :
 * chaque module porte ses propres réglages d'email, jamais un renvoi vers Paramètres > Notifications.
 * Deux onglets (Client / Établissement, demande d'Emeni). Les trois emails vivent tous dans
 * client_notification_settings (subscription_send_*, subscription_signed_received_*,
 * subscription_declined_*) — un seul endpoint à sauvegarder.
 */
export function SubscriptionEmailSettingsModal({ onClose, embarque = false }: { onClose: () => void; embarque?: boolean }) {
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [saved, setSaved] = React.useState(false);
    const [erreur, setErreur] = React.useState('');
    const [onglet, setOnglet] = React.useState<'client' | 'etablissement'>('client');

    const [sendSubject, setSendSubject] = React.useState('');
    const [sendBody, setSendBody] = React.useState('');
    const [sendButtonLabel, setSendButtonLabel] = React.useState('');
    const [sendActiveField, setSendActiveField] = React.useState<'subject' | 'body'>('body');
    const sendSubjectRef = React.useRef<HTMLInputElement>(null);
    const sendBodyRef = React.useRef<HTMLTextAreaElement>(null);

    const [notifySigned, setNotifySigned] = React.useState(true);
    const [signedSubject, setSignedSubject] = React.useState('');
    const [signedBody, setSignedBody] = React.useState('');
    const [signedActiveField, setSignedActiveField] = React.useState<'subject' | 'body'>('body');
    const signedSubjectRef = React.useRef<HTMLInputElement>(null);
    const signedBodyRef = React.useRef<HTMLTextAreaElement>(null);

    const [notifyDeclined, setNotifyDeclined] = React.useState(true);
    const [declinedSubject, setDeclinedSubject] = React.useState('');
    const [declinedBody, setDeclinedBody] = React.useState('');
    const [declinedActiveField, setDeclinedActiveField] = React.useState<'subject' | 'body'>('body');
    const declinedSubjectRef = React.useRef<HTMLInputElement>(null);
    const declinedBodyRef = React.useRef<HTMLTextAreaElement>(null);

    React.useEffect(() => {
        moduleApi.getClientNotificationSettings()
            .then((cs: any) => {
                setSendSubject(cs?.subscription_send_subject || '');
                setSendBody(cs?.subscription_send_body || '');
                setSendButtonLabel(cs?.subscription_send_button_label || '');
                setNotifySigned(cs ? !!cs.notify_subscription_signed_received : true);
                setSignedSubject(cs?.subscription_signed_received_subject || '');
                setSignedBody(cs?.subscription_signed_received_body || '');
                setNotifyDeclined(cs ? !!cs.notify_subscription_declined : true);
                setDeclinedSubject(cs?.subscription_declined_subject || '');
                setDeclinedBody(cs?.subscription_declined_body || '');
            })
            .catch((e: any) => setErreur(e?.message || 'Erreur de chargement'))
            .finally(() => setLoading(false));
    }, []);

    const enregistrer = async () => {
        setSaving(true); setErreur(''); setSaved(false);
        try {
            const res: any = await moduleApi.patchClientNotificationSettings('subscription', {
                subscription_send_subject: sendSubject, subscription_send_body: sendBody, subscription_send_button_label: sendButtonLabel,
                notify_subscription_signed_received: notifySigned, subscription_signed_received_subject: signedSubject, subscription_signed_received_body: signedBody,
                notify_subscription_declined: notifyDeclined, subscription_declined_subject: declinedSubject, subscription_declined_body: declinedBody,
            });
            if (res?.error) { setErreur(res.error); return; }
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch (e: any) {
            setErreur(e?.message || "Erreur lors de l'enregistrement.");
        } finally { setSaving(false); }
    };

    const champ = 'w-full bg-surface-soft border border-border rounded-lg px-3 py-2 text-sm text-content outline-none focus:border-brand-500';
    const onglets: { key: 'client' | 'etablissement'; label: string }[] = [
        { key: 'client', label: 'Vers le client' },
        { key: 'etablissement', label: "Vers l'établissement" },
    ];

    return (
        <FenetreEmailsModule embarque={embarque} titre="Paramètres des emails — Abonnements" icone={<Mail size={16} />} onClose={onClose}
            chargement={loading} erreur={erreur} enregistrer={enregistrer}
            etat={saving ? 'en_cours' : erreur ? 'echec' : saved ? 'fait' : 'repos'}
            avant={<Segmented value={onglet} options={onglets.map((o) => ({ value: o.key, label: o.label }))} onChange={(v) => setOnglet(v as 'client' | 'etablissement')} ariaLabel="Destinataire des emails" />}>
            {onglet === 'client' ? (
                <NotificationBlock title="Envoi du contrat" description="Part quand vous cliquez sur « Envoyer le contrat »."
                    extra={(
                        <div>
                            <label className="text-xs font-bold text-content-muted">Texte du bouton</label>
                            <input className={champ} value={sendButtonLabel} onChange={(e) => setSendButtonLabel(e.target.value)} placeholder="Consulter et signer le contrat" />
                        </div>
                    )}
                    subject={sendSubject} setSubject={setSendSubject} body={sendBody} setBody={setSendBody}
                    subjectRef={sendSubjectRef} bodyRef={sendBodyRef}
                    activeField={sendActiveField} setActiveField={setSendActiveField}
                    tokens={SUBSCRIPTION_SEND_TOKENS}
                    onInsertToken={(t) => sendActiveField === 'subject'
                        ? insertNotificationToken(sendSubjectRef.current, sendSubject, setSendSubject, t)
                        : insertNotificationToken(sendBodyRef.current, sendBody, setSendBody, t)} />
            ) : (
                <>
                    <NotificationBlock title="Contrat signé reçu" description="Reçu par l'établissement quand le client signe depuis le lien du contrat."
                        toggle={{ checked: notifySigned, onChange: setNotifySigned }}
                        subject={signedSubject} setSubject={setSignedSubject} body={signedBody} setBody={setSignedBody}
                        subjectRef={signedSubjectRef} bodyRef={signedBodyRef}
                        activeField={signedActiveField} setActiveField={setSignedActiveField}
                        tokens={SUBSCRIPTION_SIGNED_TOKENS}
                        onInsertToken={(t) => signedActiveField === 'subject'
                            ? insertNotificationToken(signedSubjectRef.current, signedSubject, setSignedSubject, t)
                            : insertNotificationToken(signedBodyRef.current, signedBody, setSignedBody, t)} />
                    <NotificationBlock title="Contrat décliné" description="Reçu par l'établissement quand le client décline le contrat."
                        toggle={{ checked: notifyDeclined, onChange: setNotifyDeclined }}
                        subject={declinedSubject} setSubject={setDeclinedSubject} body={declinedBody} setBody={setDeclinedBody}
                        subjectRef={declinedSubjectRef} bodyRef={declinedBodyRef}
                        activeField={declinedActiveField} setActiveField={setDeclinedActiveField}
                        tokens={SUBSCRIPTION_DECLINED_TOKENS}
                        onInsertToken={(t) => declinedActiveField === 'subject'
                            ? insertNotificationToken(declinedSubjectRef.current, declinedSubject, setDeclinedSubject, t)
                            : insertNotificationToken(declinedBodyRef.current, declinedBody, setDeclinedBody, t)} />
                </>
            )}
        </FenetreEmailsModule>
    );
}
